import { useEffect, useState } from "react";
import { RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { adminApi } from "../../lib/api";
import { inputCls } from "./shared";

const daysAgo = (n) => new Date(Date.now() - n * 86400000).toISOString().slice(0, 10);

export function MetaConnectTab() {
  const [merchants, setMerchants] = useState(null);
  const [start, setStart] = useState(daysAgo(30));
  const [end, setEnd] = useState(daysAgo(0));
  const [report, setReport] = useState(null);
  const [loadingReport, setLoadingReport] = useState(false);

  useEffect(() => {
    adminApi().get("/admin/metaconnect/merchants")
      .then((r) => setMerchants(r.data.merchants))
      .catch((e) => { setMerchants([]); toast.error(e.response?.data?.detail || "Failed to load merchants"); });
  }, []);

  const toggle = async (m) => {
    try {
      await adminApi().put("/admin/metaconnect/policy", { merchant: m.code, enabled: !m.enabled });
      setMerchants((ms) => ms.map((x) => (x.code === m.code ? { ...x, enabled: !m.enabled } : x)));
      toast.success(`${m.name} ${!m.enabled ? "enabled" : "hidden"} for direct offers`);
    } catch {
      toast.error("Update failed");
    }
  };

  const loadReport = () => {
    setLoadingReport(true);
    adminApi().get("/admin/metaconnect/report", { params: { start, end } })
      .then((r) => setReport(r.data))
      .catch((e) => toast.error(e.response?.data?.detail || "Report failed"))
      .finally(() => setLoadingReport(false));
  };

  const rows = report?.available ? (Array.isArray(report.report) ? report.report : report.report?.rows || report.report?.data || []) : [];
  const sum = (f) => rows.reduce((a, r) => a + (Number(r[f]) || 0), 0);

  return (
    <div className="space-y-6" data-testid="metaconnect-tab">
      <div>
        <p className="font-heading font-bold text-slate-900">Amadeus MetaConnect — airline-direct offers</p>
        <p className="text-xs text-slate-500 mt-1">
          Visitors who search see "Book direct on the airline site" offers alongside the booking engine. Toggle which airlines appear; conversions are tracked per airline in the report below and in your MetaConnect dashboard.
        </p>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg p-5">
        <p className="text-xs uppercase tracking-wide text-slate-500 font-bold mb-3">Performance report</p>
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="text-[11px] font-bold text-slate-500 uppercase tracking-wide block mb-1">From</label>
            <input type="date" data-testid="mc-report-start" className={inputCls} value={start} onChange={(e) => setStart(e.target.value)} />
          </div>
          <div>
            <label className="text-[11px] font-bold text-slate-500 uppercase tracking-wide block mb-1">To</label>
            <input type="date" data-testid="mc-report-end" className={inputCls} value={end} onChange={(e) => setEnd(e.target.value)} />
          </div>
          <button data-testid="mc-report-load-btn" onClick={loadReport} disabled={loadingReport}
            className="inline-flex items-center gap-1.5 bg-[#970C10] hover:bg-[#7A0A0D] disabled:opacity-40 text-white font-bold text-sm px-5 py-2.5 rounded-lg transition-colors duration-200">
            <RefreshCw className={`w-4 h-4 ${loadingReport ? "animate-spin" : ""}`} /> Load report
          </button>
        </div>
        {report && !report.available && (
          <p className="mt-4 text-xs bg-amber-50 border border-amber-200 text-amber-900 rounded-lg px-4 py-3" data-testid="mc-report-unavailable">
            {report.message}
          </p>
        )}
        {report?.available && (
          <div className="mt-5">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
              {[["Searches", sum("searches")], ["Clicks", sum("clicks")], ["Conversions", sum("conversions")], ["Payout", sum("payout").toFixed(2)]].map(([l, v]) => (
                <div key={l} className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                  <p className="font-heading text-xl font-bold text-slate-900">{v}</p>
                  <p className="text-[10px] uppercase tracking-wide text-slate-500 font-bold">{l}</p>
                </div>
              ))}
            </div>
            {rows.length === 0 ? (
              <p className="text-xs text-slate-400">No activity in this period yet.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm min-w-[640px]">
                  <thead className="text-left text-[10px] uppercase tracking-wide text-slate-500">
                    <tr><th className="py-2 pr-4">Date</th><th className="py-2 pr-4">Airline</th><th className="py-2 pr-4">Searches</th><th className="py-2 pr-4">Clicks</th><th className="py-2 pr-4">Conversions</th><th className="py-2 pr-4">Sale amount</th><th className="py-2">Payout</th></tr>
                  </thead>
                  <tbody>
                    {rows.map((r, i) => (
                      <tr key={i} className="border-t border-slate-100">
                        <td className="py-2 pr-4 text-xs text-slate-500">{r.date}</td>
                        <td className="py-2 pr-4 font-mono font-bold">{r.merchant_code}</td>
                        <td className="py-2 pr-4">{r.searches}</td>
                        <td className="py-2 pr-4">{r.clicks}</td>
                        <td className="py-2 pr-4 font-bold">{r.conversions}</td>
                        <td className="py-2 pr-4">{r.sale_amount} {r.currency}</td>
                        <td className="py-2">{r.payout}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="bg-white border border-slate-200 rounded-lg p-5">
        <p className="text-xs uppercase tracking-wide text-slate-500 font-bold mb-3">
          Enabled airlines {merchants ? `(${merchants.filter((m) => m.enabled).length}/${merchants.length})` : ""}
        </p>
        {!merchants && <p className="text-sm text-slate-400">Loading merchants…</p>}
        {merchants && (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {merchants.map((m) => (
              <button
                key={m.code}
                data-testid={`mc-merchant-${m.code}`}
                onClick={() => toggle(m)}
                className={`flex items-center gap-3 border rounded-lg px-3 py-2.5 text-left transition-colors duration-200 ${m.enabled ? "border-slate-200 bg-white hover:border-slate-300" : "border-slate-100 bg-slate-50 opacity-50"}`}
              >
                {m.logo && <img src={m.logo} alt={m.name} className="w-6 h-6 object-contain shrink-0" />}
                <span className="flex-1 min-w-0">
                  <span className="block text-sm font-semibold text-slate-900 truncate">{m.name}</span>
                  <span className="text-[10px] font-mono text-slate-400">{m.code}</span>
                </span>
                <span className={`text-[9px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full ${m.enabled ? "bg-green-100 text-green-700" : "bg-slate-200 text-slate-500"}`}>
                  {m.enabled ? "On" : "Off"}
                </span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
